/**
 * Phone-number normalization for the signup + OTP flows.
 *
 * The portal accepts loose user input (e.g. "(512) 555-0134", "+1 512 555 0134",
 * or just "5125550134") but the API requires strict E.164 (`+15125550134`).
 * This helper does the minimum needed normalization on the client so the API
 * regex stays strict.
 *
 * Rules:
 *   - Strip everything that isn't a digit or a leading "+".
 *   - If the result starts with "+", keep it as-is and trust the user supplied
 *     a country code.
 *   - Otherwise: 10 digits → prepend "+1" (US default). 11 digits starting
 *     with "1" → prepend "+". Anything else → return null (caller decides
 *     whether to error or pass-through).
 */
export function normalizePhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    if (digits.length < 2 || digits.length > 15) return null;
    // E.164 (and the API's E164_REGEX) requires the first digit after "+" to be
    // 1-9. Letting "+0…" through here meant the CLIENT called it valid, the
    // submit went out, and the kid read the server's rejection instead of an
    // instant field-level message.
    if (digits.startsWith("0")) return null;
    return `+${digits}`;
  }

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

/**
 * Format an E.164 number for display. Currently US-centric — anything matching
 * `+1XXXXXXXXXX` renders as `(XXX) XXX-XXXX`. Everything else returns the input.
 */
export function formatPhone(e164: string): string {
  const match = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (!match) return e164;
  return `(${match[1]}) ${match[2]}-${match[3]}`;
}

/**
 * Live, as-you-type formatter for a phone *input*. Keeps US numbers looking
 * professional — `(512) 555-0134` — while still round-tripping cleanly through
 * `normalizePhone` (which strips formatting before hitting the API).
 *
 * Behavior:
 *   - Leading "+" → treat as international: keep the "+" and raw digits only
 *     (no US grouping imposed), capped at 15 digits per E.164.
 *   - Otherwise → US: keep up to 10 digits (dropping a leading "1" country
 *     code if pasted) and group as `(XXX) XXX-XXXX` progressively.
 */
export function formatPhoneInput(raw: string): string {
  const trimmed = raw.trimStart();

  if (trimmed.startsWith("+")) {
    return "+" + trimmed.slice(1).replace(/\D/g, "").slice(0, 15);
  }

  let digits = trimmed.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);

  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** A kid-safe rendering of a parent number we only know the last four of: "(•••) ••• - 4829". */
export function formatPhoneHint(last4: string): string {
  return `(•••) ••• - ${last4}`;
}
